import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';
import { UserService } from '../store/current-user/services/user.service';
import { AuthService } from '../services/auth.service'; // Import AuthService for isSepAdmin
import { FeatureFlagsService } from '../services/feature-flag.service';

enum DetailsPagePaths {
  COURSES = 'courses/:id',
  LEARNING_PLANS = 'learningPlans/:id',
  LEARNING_PLANS_EDIT = 'learningPlans/:id/edit',
  LEARNING_PLANS_BUILDER = 'learningPlans/builder',
  ADD_PAYMENT_METHOD = 'addPaymentMethod',
  ACTIVATE_CANCELED_SUBSCRIPTION = 'reactivate',
  COURSE_SETTINGS = 'courseSettings',
}

export const detailsPageGuard: CanActivateFn = (route) => {
  const msalBroadcastService = inject(MsalBroadcastService);
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const featureFlagsService = inject(FeatureFlagsService);

  return msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    switchMap(() =>
      from(authService.isSepAdmin()).pipe(
        switchMap((isAdmin) => {
          if (isAdmin) {
            return of(true); // Admins always have access
          }

          // Fetch user data if not an admin
          return userService.getUserData().pipe(
            switchMap((userData) => {
              const [subscription] = userData.user.organization.subscriptions;
              const { isBoundToLearningPlans, freeTrialing } = subscription;
              const path = route.routeConfig?.path;
              let hasRouteAccess = false;

              if (
                path === DetailsPagePaths.LEARNING_PLANS ||
                path === DetailsPagePaths.LEARNING_PLANS_BUILDER ||
                path === DetailsPagePaths.LEARNING_PLANS_EDIT
              ) {
                hasRouteAccess = isBoundToLearningPlans;
              } else if (path === DetailsPagePaths.COURSES) {
                hasRouteAccess = !isBoundToLearningPlans;
              } else if (path === DetailsPagePaths.ADD_PAYMENT_METHOD) {
                hasRouteAccess = freeTrialing;
              } else if (
                path === DetailsPagePaths.ACTIVATE_CANCELED_SUBSCRIPTION
              ) {
                hasRouteAccess = subscription.status !== 'active';
              } else if (path === DetailsPagePaths.COURSE_SETTINGS) {
                hasRouteAccess = featureFlagsService.isFeatureEnabled(
                  featureFlagsService.courseVariables,
                );
              }
              if (!hasRouteAccess) {
                void router.navigate(['/']);
              }

              return of(hasRouteAccess);
            }),
            catchError((error) => {
              console.error('Error acquiring user data:', error);
              void router.navigate(['/']);
              return of(false); // Return false if there's an error
            }),
          );
        }),
        catchError((_error) => {
          void router.navigate(['/']);
          return of(false);
        }),
      ),
    ),
  );
};
